/**
 * SitePhoneManager Component
 *
 * Manages multiple phone/fax numbers for a site.
 *
 * IMPORTANT: Use this component instead of single phone/fax input fields.
 * - Each site can have multiple phones AND multiple faxes
 * - Confirmation is per-site-phone association (not global per number)
 * - Purpose can be multi-select for multiple purposes
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Fax as FaxIcon,
  Edit as EditIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { PatientService } from '../../services/patient.service';
import {
  SitePhone,
  PHONE_PURPOSES,
  getConfirmationStatus,
} from '../../model/provider.types';

const patientService = new PatientService();

interface SitePhoneManagerProps {
  siteId: number;
  readOnly?: boolean;
  onPhoneChange?: () => void;
}

export const SitePhoneManager: React.FC<SitePhoneManagerProps> = ({
  siteId,
  readOnly = false,
  onPhoneChange,
}) => {
  const [phones, setPhones] = useState<SitePhone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingPhone, setEditingPhone] = useState<SitePhone | null>(null);

  // Form state for add/edit
  const [formNumber, setFormNumber] = useState('');
  const [formNumberType, setFormNumberType] = useState<'phone' | 'fax'>('phone');
  const [formPurposes, setFormPurposes] = useState<string[]>(['general']);
  const [formNotes, setFormNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPhones = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await patientService.getSitePhones(siteId);
      setPhones(response.data.phones || []);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadPhones();
  }, [loadPhones]);

  const resetForm = () => {
    setFormNumber('');
    setFormNumberType('phone');
    setFormPurposes(['general']);
    setFormNotes('');
    setEditingPhone(null);
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (phone: SitePhone) => {
    setEditingPhone(phone);
    setFormPurposes(phone.purpose.split(',').map(p => p.trim()).filter(Boolean));
    setFormNotes(phone.notes || '');
    setAddDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setAddDialogOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (editingPhone) {
        // Update existing
        await patientService.updateSitePhone(editingPhone.id, {
          purpose: formPurposes.join(', '),
          notes: formNotes || undefined,
        });
      } else {
        // Add new
        await patientService.addSitePhone(siteId, {
          number: formNumber,
          number_type: formNumberType,
          purpose: formPurposes.join(', '),
          notes: formNotes || undefined,
        });
      }

      handleCloseDialog();
      loadPhones();
      onPhoneChange?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async (phone: SitePhone) => {
    try {
      await patientService.confirmSitePhone(phone.id, 'manual');
      loadPhones();
      onPhoneChange?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to confirm');
    }
  };

  const handleDelete = async (phone: SitePhone) => {
    if (!window.confirm(`Delete ${phone.number_type} ${phone.number}?`)) return;

    try {
      await patientService.deleteSitePhone(phone.id);
      loadPhones();
      onPhoneChange?.();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete');
    }
  };

  const getConfirmationIcon = (phone: SitePhone) => {
    const status = getConfirmationStatus(phone.confirmed_at);
    switch (status) {
      case 'fresh':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'stale':
        return <RadioButtonUncheckedIcon color="warning" fontSize="small" />;
      case 'never':
      default:
        return <RemoveIcon color="disabled" fontSize="small" />;
    }
  };

  const formatPhoneNumber = (number: string) => {
    if (number.length === 10) {
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    }
    return number;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2">Phone/Fax Numbers</Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add
          </Button>
        )}
      </Box>

      {phones.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No phone numbers added
        </Typography>
      ) : (
        <List dense>
          {phones.map((phone) => (
            <ListItem key={phone.id} sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {phone.number_type === 'phone' ? (
                  <PhoneIcon fontSize="small" color="action" />
                ) : (
                  <FaxIcon fontSize="small" color="action" />
                )}
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Tooltip
                  title={
                    phone.confirmed_at
                      ? `Confirmed: ${new Date(phone.confirmed_at).toLocaleDateString()}`
                      : 'Not confirmed'
                  }
                >
                  {getConfirmationIcon(phone)}
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box component="span" display="flex" alignItems="center" gap={0.5}>
                    {formatPhoneNumber(phone.number)}
                    {phone.shared_with && phone.shared_with.length > 0 && (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" fontWeight={500}>
                              Shared with {phone.shared_with.length} other site{phone.shared_with.length > 1 ? 's' : ''}:
                            </Typography>
                            {phone.shared_with.map((s) => (
                              <Typography key={s.site_id} variant="caption" display="block">
                                • {s.site_name}
                              </Typography>
                            ))}
                          </Box>
                        }
                      >
                        <ShareIcon fontSize="small" color="info" sx={{ fontSize: 16 }} />
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={
                  <Box component="span">
                    {phone.purpose.split(',').map((p, i) => (
                      <Chip
                        key={i}
                        label={p.trim()}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                }
              />
              {!readOnly && (
                <ListItemSecondaryAction>
                  <Tooltip title="Confirm">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleConfirm(phone)}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleOpenEditDialog(phone)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDelete(phone)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPhone ? 'Edit Phone/Fax' : 'Add Phone/Fax'}
        </DialogTitle>
        <DialogContent>
          {!editingPhone && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Phone Number"
                fullWidth
                value={formNumber}
                onChange={(e) => setFormNumber(e.target.value)}
                placeholder="(555) 123-4567"
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Type</InputLabel>
                <Select
                  value={formNumberType}
                  label="Type"
                  onChange={(e) => setFormNumberType(e.target.value as 'phone' | 'fax')}
                >
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="fax">Fax</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Purpose</InputLabel>
            <Select
              multiple
              value={formPurposes}
              label="Purpose"
              onChange={(e) => setFormPurposes(e.target.value as string[])}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {PHONE_PURPOSES.map((purpose) => (
                <MenuItem key={purpose} value={purpose}>
                  {purpose}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={2}
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || (!editingPhone && !formNumber)}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SitePhoneManager;
